import { Grid3x3, Square } from "lucide-react";
import { sizeSteps, type GalleryColumns, type GalleryScreen } from "@/lib/galleryDensity";
import { cn } from "@/lib/utils";

interface Props {
  screen: GalleryScreen;
  value: GalleryColumns;
  onChange: (columns: GalleryColumns) => void;
  className?: string;
}

const STEP_BUTTON =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-studio-muted transition-colors hover:bg-studio-card-hover hover:text-studio-ink focus-visible:outline-none focus-visible:shadow-glow-sm disabled:opacity-40 disabled:hover:bg-transparent sm:h-8 sm:w-8";

/** Item size for the gallery grid, like zooming a phone's photo library. */
export function GallerySizeSlider({ screen, value, onChange, className }: Props) {
  const steps = sizeSteps(screen);
  const index = Math.max(0, steps.indexOf(value));
  const last = steps.length - 1;
  const describe = (columns: GalleryColumns) => `${columns} ${columns === 1 ? "column" : "columns"}`;
  return (
    <div
      data-testid="gallery-size"
      className={cn("inline-flex items-center gap-1 rounded-xl border border-studio-border bg-studio-card p-1", className)}
    >
      <button type="button" aria-label="Smaller items" title="Smaller items" disabled={index === 0}
        onClick={() => onChange(steps[Math.max(0, index - 1)])} className={STEP_BUTTON}>
        <Grid3x3 className="h-4 w-4" />
      </button>
      <input
        type="range"
        aria-label="Item size"
        aria-valuetext={describe(value)}
        min={0}
        max={last}
        step={1}
        value={index}
        onChange={(event) => onChange(steps[Number(event.target.value)])}
        className="h-1.5 w-24 cursor-pointer accent-studio-accent sm:w-28"
      />
      <button type="button" aria-label="Larger items" title="Larger items" disabled={index === last}
        onClick={() => onChange(steps[Math.min(last, index + 1)])} className={STEP_BUTTON}>
        <Square className="h-4 w-4" />
      </button>
    </div>
  );
}
